import type { BuyerField, OpportunityField, ParcelField, TransactionField } from "@/graphql"
import type { TransactionVerificationStatus } from "@/types/filters"

export const ViewScope = {
	OpportunitiesMap: "OPPORTUNITIES_MAP",
	OpportunitiesHuntingMap: "OPPORTUNITIES_HUNTING_MAP",
	OpportunitiesTable: "OPPORTUNITIES_TABLE",
	OpportunitiesHuntingTable: "OPPORTUNITIES_HUNTING_TABLE",
	TransactionsMap: "TRANSACTIONS_MAP",
	TransactionsHuntingMap: "TRANSACTIONS_HUNTING_MAP",
	TransactionsTable: "TRANSACTIONS_TABLE",
	BuyersTable: "BUYERS_TABLE",
	ParcelsMap: "PARCELS_MAP",
	ParcelsHuntingMap: "PARCELS_HUNTING_MAP",
} as const

export type ViewScope = (typeof ViewScope)[keyof typeof ViewScope]

export enum ParcelBooleanFilterField {
	PpuArea = "ppuArea",
	TodArea = "todArea",
	SoilContaminated = "soilContaminated",
	Wetland = "wetland",
	Rainette = "rainette",
}

type FloatRange = {
	min?: number | null
	max?: number | null
}

type DateRange = {
	min?: string | null
	max?: string | null
}

type ViewFilter = {
	opportunity?: {
		opportunityTypes?: string[]
		developmentTypes?: string[]
		regions?: string[]
		municipalities?: string[]
		boroughs?: string[]
		price?: FloatRange
		landArea?: FloatRange
		buildableLandArea?: FloatRange
		residentialUnits?: FloatRange
		launchDate?: DateRange
		assetTypes?: string[]
		landCategories?: string[]
		landSubcategories?: string[]
		evaluationStatus?: string[]
		priority?: string | null
		brokers?: string[]
	}
	transaction?: {
		assetTypes?: string[]
		builtCategories?: string[]
		builtSubcategories?: string[]
		landCategories?: string[]
		landSubcategories?: string[]
		statuses?: string[]
		verificationStatus?: TransactionVerificationStatus | null
		buyerStatus?: string | null
		source?: string | null
		composition?: string | null
		price?: FloatRange
		landArea?: FloatRange
		buildableLandArea?: FloatRange
		residentialUnits?: FloatRange
		date?: DateRange
		regions?: string[]
		municipalities?: string[]
		boroughs?: string[]
		search?: string | null
	}
	buyer?: {
		hasContactDetails?: boolean
		transaction?: {
			assetTypes?: string[]
			landCategories?: string[]
			landSubcategories?: string[]
			statuses?: string[]
			verificationStatus?: TransactionVerificationStatus | null
			buyerStatus?: string | null
			source?: string | null
			composition?: string | null
			price?: FloatRange
			landArea?: FloatRange
			buildableLandArea?: FloatRange
			residentialUnits?: FloatRange
			date?: DateRange
			regions?: string[]
			municipalities?: string[]
			boroughs?: string[]
		}
	}
	parcel?: {
		superficy?: FloatRange
		dwellingUnits?: FloatRange
		storeys?: FloatRange
		propertyValue?: FloatRange
		buildingAge?: FloatRange
		showStreets?: boolean
	} & { [Field in ParcelBooleanFilterField]?: boolean | null }
}

export type View = {
	scope: ViewScope
	select?: {
		opportunity?: OpportunityField[]
		transaction?: TransactionField[]
		parcel?: ParcelField[]
		buyer?: BuyerField[]
	}
	filter?: ViewFilter
	order?: Array<{
		field: string
		direction?: "asc" | "desc"
	}>
	columnOverrides?: Record<string, { group?: string; visible?: boolean }>
}

// local-only fields above — not part of the backend's ViewInput, must stay out of API requests (see toApiViewInput)
export const CLIENT_ONLY_VIEW_KEYS: (keyof View)[] = ["columnOverrides"]
